import { action, computed, makeObservable, observable } from 'mobx';

import { OptionShape } from 'app/types/reference';
import { Reference } from 'app/model/Reference';
import { LocalityReference } from 'app/model/LocalityReference';
import { ReferenceService } from 'app/service/ReferenceService';
import { LocalityAddress } from 'app/model/LocalityAddress';
import { Store } from 'app/store/Store';
import { LocalityTypeStore } from 'app/logic/address/LocalityTypeStore';

export class BirthPlaceFormStore implements Store<LocalityAddress> {
    private readonly referenceService: ReferenceService =
        new ReferenceService();

    /**
     * Выбранное значение "Страна рождение" из справочника
     * @private
     */
    @observable private _birthCountry: Reference;

    /**
     * Выбранное значение "Населённый пункт" из справочника
     * @private
     */
    @observable private _belarusBirthLocality: LocalityReference;

    /**
     * Введенное пользователем значение "Наименование населённого пункта"
     * @private
     */
    @observable private _localityTypeCode: Reference;

    /**
     * Выбранное из справочника значение "Тип населённого пункта"
     * @private
     */
    @observable private _localityName: string;

    constructor(
        private readonly _birthLocality: LocalityAddress,
        private readonly _localityTypesReference: Array<Reference>,
        private readonly _localityTypesStore: LocalityTypeStore,
    ) {
        makeObservable(this);
    }

    @action
    initialize() {
        if (!this._birthLocality) {
            return Promise.resolve();
        }

        if (this._birthLocality.isBelarus) {
            return Promise.all([
                this.referenceService.getCountryById(
                    this._birthLocality.countryCode,
                ),
                this.referenceService.getLocalityBySoatoCode(
                    this._birthLocality.soatoCode,
                ),
            ]).then(([countryReference, localityReference]) => {
                this.setBirthCountry(countryReference);
                this.setBelarusBirthLocality(localityReference);
            });
        } else {
            return Promise.all([
                this.referenceService.getCountryById(
                    this._birthLocality.countryCode,
                ),
                this.referenceService.getLocalityTypeById(
                    this._birthLocality.localityType,
                ),
            ]).then(([birthCountry, localityTypeCode]) => {
                this.setBirthCountry(birthCountry);
                this.setLocalityTypeCode(localityTypeCode);
                this.setLocalityName(this._birthLocality.locality);
            });
        }
    }

    getModel(): LocalityAddress {
        return this.isBelarusBirthCountry
            ? LocalityAddress.reference(
                  this._belarusBirthLocality,
                  this._birthCountry,
              )
            : new LocalityAddress(
                  null,
                  this.birthCountry.id,
                  this._localityTypeCode.id,
                  this._localityName,
              );
    }

    @computed
    get localityTypesReference(): Array<OptionShape> {
        return this._localityTypesReference.map(this.localityTypeAsOptionShape);
    }

    get localityTypesStore(): LocalityTypeStore {
        return this._localityTypesStore;
    }

    localityTypeAsOptionShape(reference: Reference): OptionShape {
        return !!reference
            ? { key: reference.id, content: reference.name }
            : null;
    }

    getLocalityTypeByKey(key: string): Reference {
        return this._localityTypesReference.find((reference) => {
            return reference.id === key;
        });
    }

    @computed
    get isBelarusBirthCountry() {
        return (
            !!this._birthCountry &&
            this._birthCountry.id === LocalityAddress.BELARUS_COUNTRY_CODE
        );
    }

    @computed
    get isForeignBirthCountry() {
        return (
            !!this._birthCountry &&
            this._birthCountry.id !== LocalityAddress.BELARUS_COUNTRY_CODE
        );
    }

    @computed
    get birthCountry(): Reference {
        return this._birthCountry;
    }

    @action
    setBirthCountry(value: Reference) {
        this._birthCountry = value;
    }

    @computed
    get belarusBirthLocality(): LocalityReference {
        return this._belarusBirthLocality;
    }

    @action
    setBelarusBirthLocality(value: LocalityReference) {
        this._belarusBirthLocality = value;
    }

    @computed
    get localityTypeCode(): Reference {
        return this._localityTypeCode;
    }

    @action
    setLocalityTypeCode(value: Reference) {
        this._localityTypeCode = value;
    }

    @computed
    get localityName(): string {
        return this._localityName;
    }

    @action
    setLocalityName = (value: string) => {
        this._localityName = value;
    };
}
